import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map, Observable, of, shareReplay, tap } from 'rxjs'
import { environment } from 'src/environments/environment'

export interface CapacitySubmissionConsentValues {
    consentAccepted: boolean
    termsAndConditionsAccepted: boolean
    declarationOfTruthAccepted: boolean
}

export interface CapacitySubmissionChangedField {
    field: string
    source: string
    original: string
    submitted: string
}

export interface CapacitySubmissionRequest {
    installationNumber: string
    contactName: string
    contactEmail: string
    contactPhone: string
    addOnContractEmail: string
    companyName: string
    cvrNumber: string
    installationAddress: string
    requestedCapacity: string
    actualInstalledEffect?: string
    currentCapacity?: string | null
    currentCapacityUnit?: string | null
    meterSize?: string | null
    consentAccepted: boolean
    termsAndConditionsAccepted: boolean
    declarationOfTruthAccepted: boolean
}

export interface CapacitySubmissionStatusResponse {
    available: boolean
    eligible: boolean
    helpEmail?: string | null
    installationNumber?: string | null
    installationAddress?: string | null
    companyName?: string | null
    cvrNumber?: string | null
    currentCapacity?: CapacitySubmissionCapacity | null
    meterSize?: string | null
    installedEffect?: CapacitySubmissionInstalledEffect | null
    warnings?: CapacitySubmissionDataIssue[] | null
    errors?: CapacitySubmissionDataIssue[] | null
    actualInstalledEffect?: string | null
    startDate?: string | null
    endDate?: string | null
    changedPrefilledFields?: CapacitySubmissionChangedField[] | null
}

export interface CapacitySubmissionCapacity {
    value: number
    unit: string
    fromDate: string
}

export interface CapacitySubmissionInstalledEffect {
    status: string
    source: string
    value?: string | null
    unit?: string | null
    manualEntryAllowed: boolean
}

export interface CapacitySubmissionDataIssue {
    code: string
    message: string
}

export interface CapacitySubmissionResponse {
    reference: string
    submittedAt: string
    status: string
    forwardingStatus: string
    changedPrefilledFields?: CapacitySubmissionChangedField[] | null
}

@Injectable({
  providedIn: 'root',
})
export class CapacitySubmissionService {
  private http = inject(HttpClient)
  private readonly statusCache = new Map<string, Observable<CapacitySubmissionStatusResponse>>()

  public getStatus(installationNumber: string): Observable<CapacitySubmissionStatusResponse> {
    if (!installationNumber?.trim()) {
      return of({ available: false, eligible: false })
    }

    const cachedStatus$ = this.statusCache.get(installationNumber)
    if (cachedStatus$) {
      return cachedStatus$
    }

    let status$: Observable<CapacitySubmissionStatusResponse>
    status$ = this.requestStatus(installationNumber).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      tap({
        error: () => {
          if (this.statusCache.get(installationNumber) === status$) {
            this.statusCache.delete(installationNumber)
          }
        },
      })
    )
    this.statusCache.set(installationNumber, status$)

    return status$
  }

  public invalidateStatus(installationNumber: string): void {
    if (installationNumber) {
      this.statusCache.delete(installationNumber)
    }
  }

  public submit(request: CapacitySubmissionRequest): Observable<CapacitySubmissionResponse> {
    return this.http
      .post<CapacitySubmissionResponse>(`${environment.API_BASE_PATH}/api/capacity-submissions`, request)
      .pipe(
        map((response) => this.mapSubmissionResponse(response)),
        tap(() => this.invalidateStatus(request.installationNumber))
      )
  }

  private requestStatus(installationNumber: string): Observable<CapacitySubmissionStatusResponse> {
    return this.http
      .get<CapacitySubmissionStatusResponse>(`${environment.API_BASE_PATH}/api/capacity-submissions/status`, {
        params: { installationNumber },
      })
      .pipe(map((response) => this.mapStatusResponse(response)))
  }

  private mapStatusResponse(response: CapacitySubmissionStatusResponse): CapacitySubmissionStatusResponse {
    return {
      ...response,
      warnings: (response.warnings ?? []).filter((issue) => issue.code !== 'installed-effect-manual-fallback'),
      errors: response.errors ?? [],
      startDate: response.startDate ?? null,
      endDate: response.endDate ?? null,
      changedPrefilledFields: response.changedPrefilledFields ?? [],
    }
  }

  private mapSubmissionResponse(response: CapacitySubmissionResponse): CapacitySubmissionResponse {
    return {
      ...response,
      changedPrefilledFields: response.changedPrefilledFields ?? [],
    }
  }
}
